import { supabase } from "@cl/supabase";
import {
  LEAD_STATUSES,
  type LeadLanguage,
  type LeadStatus,
} from "@u/leadStatus";
import { tourLeadSchema } from "@u/leadValidation";
import { normalizeEmail } from "@u/contactIdentity";

export { LEAD_STATUSES };
export type { LeadStatus };

export interface TourLead {
  id: string;
  tour_id: string;
  /** Contact this lead resolved to (CRM). Nullable: pre-CRM rows. */
  contact_id: string | null;
  session_id: string | null;
  name: string | null;
  email: string | null;
  phone: string | null;
  message: string | null;
  preferred_language: LeadLanguage | null;
  status: LeadStatus;
  created_at: string;
}

export interface InsertLeadPayload {
  tour_id: string;
  session_id?: string | null;
  name?: string;
  email?: string;
  phone?: string;
  message?: string;
  preferred_language?: LeadLanguage;
  /** Set when this lead is an Open House RSVP. */
  open_house_event_id?: string | null;
}

/** True when a public-form submission failed the edge fn's rate limit (429).
 *  supabase-js wraps the HTTP failure as FunctionsHttpError with the raw
 *  Response on `.context`. Lets callers show a "wait a bit" message instead of
 *  a generic error. */
export function isRateLimitError(err: unknown): boolean {
  return (err as { context?: { status?: number } })?.context?.status === 429;
}

export async function insertLead(
  payload: InsertLeadPayload,
  captchaToken: string,
): Promise<void> {
  // Validate + sanitize client-side, then submit through the abuse-protected
  // gateway (Turnstile + rate limit + service-role insert). Anon INSERT on
  // tour_leads is revoked — this is the only write path.
  const clean = tourLeadSchema.parse(payload);
  const { error } = await supabase.functions.invoke("submit-public-form", {
    body: { form: "tour_lead", payload: clean, captchaToken },
  });
  if (error) throw error;
}

export async function fetchTourLeads(tourId: string): Promise<TourLead[]> {
  const { data, error } = await supabase
    .from("tour_leads")
    .select("*")
    .eq("tour_id", tourId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []).map((l) => ({
    ...(l as TourLead),
    status: ((l as TourLead).status as LeadStatus) ?? "new",
  }));
}

type LeadPatch = {
  status?: LeadStatus;
  // Owner-editable contact fields (corrections / completing a manual lead).
  // RLS scopes UPDATE to the owner; the anon insert lock only covers INSERT.
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  message?: string | null;
};

/** Owner edit of a lead's contact fields. Trims, empties → null. Routes to the
 *  right table by source. */
export async function updateLeadContact(
  source: LeadSource,
  id: string,
  input: { name?: string; email?: string; phone?: string; message?: string },
): Promise<void> {
  const patch: LeadPatch = {
    name: input.name?.trim() || null,
    email: normalizeEmail(input.email),
    phone: input.phone?.trim() || null,
    message: input.message?.trim() || null,
  };
  return updateUnifiedLead(source, id, patch);
}

export async function updateLead(id: string, patch: LeadPatch): Promise<void> {
  const { error } = await supabase
    .from("tour_leads")
    .update(patch)
    .eq("id", id);
  if (error) throw error;
}

// ── Unified leads (/dashboard/leads) ───────────────────────────────────────

export type LeadSource = "360";

export interface UnifiedLead {
  id: string;
  source: LeadSource;
  /** tour_id */
  source_id: string;
  /** tour title */
  source_name: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  message: string | null;

  preferred_language: LeadLanguage | null;
  /** .360 analytics viewing session the lead was captured in (null for .tap).
   *  Drives the "how much did they tour" chip in the detail. */
  session_id: string | null;
  status: LeadStatus;
  /** Acquisition channel ("origen") — viewer (auto) o un canal manual. */
  channel: string;
  /** Set when the lead is an Open House RSVP → links to open_house_events. */
  open_house_event_id: string | null;
  /** CRM contact the resolver deduped this lead into (null pre-resolver). */
  contact_id: string | null;
  created_at: string;
  /** Most recent touch. `lead_activity` se dropeó (20260914171227) y el
   *  timeline vive en `contact_activity` vía contact_id, así que hoy es
   *  siempre `created_at`. El campo queda porque ordena el listado. */
  last_activity_at: string;
}

/**
 * All leads owned by the current user across both products. RLS already scopes
 * each table to the owner, so no explicit user filter is needed here.
 */
export async function fetchAllUserLeads(): Promise<UnifiedLead[]> {
  const tourRes = await supabase
    .from("tour_leads")
    .select("*, tours!inner(title)")
    .order("created_at", { ascending: false });

  // Degrada a lista vacía + warn.
  if (tourRes.error) console.warn("fetchAllUserLeads: tour_leads", tourRes.error);

  const tourLeads: UnifiedLead[] = (tourRes.data ?? []).map(
    (l: Record<string, unknown> & { tours?: { title?: string } }) => ({
      id: l.id as string,
      source: "360" as const,
      source_id: l.tour_id as string,
      source_name: l.tours?.title ?? "—",
      name: (l.name as string | null) ?? null,
      email: (l.email as string | null) ?? null,
      phone: (l.phone as string | null) ?? null,
      message: (l.message as string | null) ?? null,
      preferred_language: (l.preferred_language as LeadLanguage | null) ?? null,
      session_id: (l.session_id as string | null) ?? null,
      status: (l.status as LeadStatus) ?? "new",
      channel: (l.channel as string | null) ?? "viewer",
      open_house_event_id: (l.open_house_event_id as string | null) ?? null,
      contact_id: (l.contact_id as string | null) ?? null,
      created_at: l.created_at as string,
      last_activity_at: l.created_at as string,
    }),
  );

  return [...tourLeads].sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );
}

/** Route a status update to the right table based on the source. */
export async function updateUnifiedLead(
  source: LeadSource,
  id: string,
  patch: LeadPatch,
): Promise<void> {
  void source;
  return updateLead(id, patch);
}

/** Owner-created (manual) lead — walk-in / phone / referral. Routes to the right
 *  table via the owner-scoped RPC (ownership enforced server-side). */
export async function createOwnerLead(input: {
  source: LeadSource;
  sourceId: string;
  channel: string;
  name?: string;
  email?: string;
  phone?: string;
  message?: string;
}): Promise<void> {
  const { error } = await supabase.rpc("create_owner_lead", {
    p_source: input.source,
    p_source_id: input.sourceId,
    p_channel: input.channel,
    p_name: input.name?.trim() || null,
    p_email: input.email?.trim() || null,
    p_phone: input.phone?.trim() || null,
    p_message: input.message?.trim() || null,
  });
  if (error) throw error;
}

/** Delete a lead from the right table based on its source (RLS scopes to owner). */
export async function deleteUnifiedLead(
  source: LeadSource,
  id: string,
): Promise<void> {
  void source;
  const { error } = await supabase.from("tour_leads").delete().eq("id", id);
  if (error) throw error;
}
